/**
 * Notification Manager Service
 * Handles automatic notification registration/unregistration during auth flows
 */

import { pushNotificationService } from './pushNotification.service';

export const notificationManager = {
  /**
   * Initialize notifications after successful login
   */
  onUserLogin: async (userId: string): Promise<void> => {
    try {
      console.log('Initializing notifications for user:', userId);
      
      // Check if notifications are supported
      const isSupported = await pushNotificationService.isSupported();
      if (!isSupported) {
        console.log('Push notifications not supported, skipping initialization');
        return;
      }

      // Get notification settings
      const settings = pushNotificationService.getNotificationSettings();
      const isPermissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      
      // Auto-register if user enabled notifications or permission is granted
      if (settings.enabled || isPermissionGranted) {
        console.log('Auto-registering notifications for returning user');
        await pushNotificationService.initialize(userId);
      } else {
        console.log('Notifications not enabled by user, skipping auto-registration');
      }
    } catch (error) {
      console.error('Error initializing notifications on login:', error);
      // Don't throw error as login should succeed even if notifications fail
    }
  },

  /**
   * Clean up notifications and service worker on user logout
   */
  onUserLogout: async (): Promise<void> => {
    try {
      console.log('Cleaning up notifications and service worker on logout');
      
      // Delete token, unregister token from backend, and unregister service worker
      await pushNotificationService.deleteToken();
      await pushNotificationService.unregisterServiceWorker();

      // Clear local FCM token and registration status, but PRESERVE user's notification enabled settings
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_registration_status');
      
      console.log('Notification cleanup completed on logout');
    } catch (error) {
      console.error('Error cleaning up notifications on logout:', error);
      // Don't throw error as logout should succeed even if notification cleanup fails
    }
  },

  /**
   * Handle notification permission granted
   */
  onPermissionGranted: async (userId?: string): Promise<string | null> => {
    try {
      console.log('Notification permission granted, initializing...');
      return await pushNotificationService.initialize(userId);
    } catch (error) {
      console.error('Error handling permission granted:', error);
      throw error;
    }
  },

  /**
   * Handle notification permission denied
   */
  onPermissionDenied: (): void => {
    try {
      console.log('Notification permission denied, updating settings');
      
      const settings = pushNotificationService.getNotificationSettings();
      pushNotificationService.updateNotificationSettings({
        ...settings,
        enabled: false
      });
    } catch (error) {
      console.error('Error handling permission denied:', error);
    }
  },

  /**
   * Re-register token after token refresh or app update
   */
  refreshRegistration: async (userId: string): Promise<string | null> => {
    try {
      console.log('Refreshing notification registration');
      
      // Get fresh token
      const token = await pushNotificationService.getToken();
      
      if (token) {
        // Re-register with backend
        await pushNotificationService.registerTokenWithBackend(token, userId);
        console.log('Notification registration refreshed');
        return token;
      }
      
      return null;
    } catch (error) {
      console.error('Error refreshing notification registration:', error);
      throw error;
    }
  },

  /**
   * Check if user should be prompted for notification permission
   */
  shouldPromptForPermission: (): boolean => {
    try {
      const permissionState = pushNotificationService.getPermissionState();
      const settings = pushNotificationService.getNotificationSettings();
      
      // Prompt if:
      // 1. Notifications are supported
      // 2. Permission is default (not granted or denied)
      // 3. User hasn't explicitly disabled notifications
      return (
        permissionState.isSupported &&
        permissionState.permission === 'default' &&
        !settings.hasOwnProperty('enabled') // User hasn't made a choice yet
      );
    } catch (error) {
      console.error('Error checking if should prompt for permission:', error);
      return false;
    }
  }
};
